import { useState, useCallback } from "react";

import type { OptionType } from "@impero-com/common/components/form/fields/types";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { Button, Alert, Tooltip } from "@impero-com/ui";
import { Input } from "antd";

import {
    ControlReassignment,
    ExistingAssignmentsPolicy,
} from "api/generated/model";
import { RadioButtonGroupInput } from "components/form/fields/radioButtonGroupInput";

import { useSubmitBulkAssignMutation } from "../queries/useBulkAssignMutation";
import { BulkAssignStep } from "../stepsContainer";

import * as theme from "./theme.css";

const CONFIRMATION_TEXT = "IMPERO";

interface ConfirmationStepProps {
    controlReassignments: Record<string, ControlReassignment[]> | null;
    onStepChange: (step: BulkAssignStep) => void;
}

function getDisabledReasons(
    policy: ExistingAssignmentsPolicy | null,
    isConfirmed: boolean,
    t: TFunction
): string {
    const reasons: string[] = [];

    if (!policy) {
        reasons.push(
            t("ControlsBulkAssign_ConfirmationStep_TooltipSelectPolicy")
        );
    }
    if (!isConfirmed) {
        reasons.push(
            t("ControlsBulkAssign_ConfirmationStep_TooltipTypeConfirmation")
        );
    }

    return reasons.join("\n");
}

export function ConfirmationStep({
    controlReassignments,
    onStepChange,
}: ConfirmationStepProps) {
    const { t } = useTranslation();
    const [policy, setPolicy] = useState<ExistingAssignmentsPolicy | null>(
        null
    );
    const [confirmText, setConfirmText] = useState("");
    const { mutate: submitBulkAssign, isPending } =
        useSubmitBulkAssignMutation();

    const policyOptions: OptionType<ExistingAssignmentsPolicy>[] = [
        {
            value: "Keep",
            label: t(
                "ControlsBulkAssign_ConfirmationStep_ExistingAssignmentsKeep"
            ),
        },
        {
            value: "Remove",
            label: t(
                "ControlsBulkAssign_ConfirmationStep_ExistingAssignmentsRemove"
            ),
        },
    ];

    const isConfirmed = confirmText === CONFIRMATION_TEXT;

    const handlePolicyChange = (
        newPolicy: ExistingAssignmentsPolicy | null
    ) => {
        setPolicy(newPolicy);
    };

    const handleSubmit = useCallback(() => {
        if (!controlReassignments || !isConfirmed || !policy || isPending) {
            return;
        }

        submitBulkAssign({
            policy,
            controlReassignments,
            onSuccess: (step) => {
                setConfirmText("");
                onStepChange(step);
            },
        });
    }, [
        controlReassignments,
        isConfirmed,
        policy,
        isPending,
        submitBulkAssign,
        setConfirmText,
        onStepChange,
    ]);

    const handleBack = () => {
        onStepChange("upload");
    };

    return (
        <div className={theme.stepContainer}>
            <h2>{t("ControlsBulkAssign_ConfirmationStep_Title")}</h2>
            <Alert
                showIcon={true}
                type="info"
                message={t("ControlsBulkAssign_ConfirmationStep_Instructions")}
                className={theme.infoAlertBox}
            />

            <div className={theme.confirmSection}>
                <h3>
                    {t(
                        "ControlsBulkAssign_ConfirmationStep_ExistingAssignmentTitle"
                    )}
                </h3>
                <RadioButtonGroupInput<ExistingAssignmentsPolicy>
                    value={policy}
                    onChange={handlePolicyChange}
                    options={policyOptions}
                    allowDeselect={false}
                />
            </div>

            <div className={theme.confirmSection}>
                <h3>{t("ControlsBulkAssign_ConfirmationStep_TypeConfirm")}</h3>
                <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={CONFIRMATION_TEXT}
                />
            </div>

            <div className={theme.buttonGroup}>
                <Button onClick={handleBack} disabled={isPending}>
                    {t("Back")}
                </Button>
                <Tooltip
                    title={getDisabledReasons(policy, isConfirmed, t)}
                    open={!isConfirmed || !policy ? undefined : false}
                >
                    <Button
                        variant="primary"
                        disabled={!isConfirmed || !policy || isPending}
                        onClick={handleSubmit}
                        loading={isPending}
                    >
                        {t("ControlsBulkAssign_ConfirmationStep_Update")}
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
}
